"use client"

import { useEffect, useRef } from "react"
import { playScoreBlipSound } from "@/hooks/use-sound-effects"

interface AbductionScoreOverlayProps {
  startScore: number
  enemyScore: number
  towerScore: number
  progress: number // 0-1
}

export const AbductionScoreOverlay = ({
  startScore,
  enemyScore,
  towerScore,
  progress,
}: AbductionScoreOverlayProps) => {
  // First half: count enemy score, second half: count tower score
  const enemyProgress = Math.min(progress * 2, 1) // 0-1 during first half
  const towerProgress = Math.max((progress - 0.5) * 2, 0) // 0-1 during second half

  const currentEnemyScore = Math.floor(enemyScore * enemyProgress)
  const currentTowerScore = Math.floor(towerScore * towerProgress)
  const currentTotal = startScore + currentEnemyScore + currentTowerScore

  // Play blip sound while score is counting (progress < 1)
  const lastBlipTime = useRef(0)
  useEffect(() => {
    if (progress >= 1) return // Done counting

    const now = Date.now()
    const blipInterval = 80 // Play blip every 80ms while counting
    if (now - lastBlipTime.current >= blipInterval) {
      playScoreBlipSound(0.3)
      lastBlipTime.current = now
    }
  }, [progress, currentTotal])

  return (
    <div className="absolute inset-0 flex items-center justify-center z-[100] pointer-events-none">
      <div className="bg-black/70 border-2 border-amber-500/50 rounded-xl px-8 py-6 text-center animate-in fade-in zoom-in-95 duration-300">
        <div className="text-amber-300 text-sm font-bold mb-4">LEVEL COMPLETE</div>

        <div className="space-y-2 text-left mb-4">
          <div className="flex justify-between gap-8">
            <span className="text-gray-400">Enemies:</span>
            <span className="text-white font-bold tabular-nums">+{currentEnemyScore.toLocaleString()}</span>
          </div>
          <div className="flex justify-between gap-8">
            <span className="text-gray-400">Towers:</span>
            <span className="text-white font-bold tabular-nums">+{currentTowerScore.toLocaleString()}</span>
          </div>
        </div>

        <div className="border-t border-amber-500/30 pt-3">
          <div className="text-white text-3xl font-bold tabular-nums">
            {currentTotal.toLocaleString()}
          </div>
          <div className="text-amber-400/70 text-xs mt-1">TOTAL SCORE</div>
        </div>
      </div>
    </div>
  )
}
